import { Controller } from "@hotwired/stimulus";

// Free-form spot layout editor with smart guides + spacing measurement.
// Alignment lines appear when dragging near another spot's axis or canvas center.
// Distance markers show the gap to nearby spots on the same row/column.
// Equal-spacing snap when positioned equidistant between two siblings.
export default class extends Controller {
  static targets = [
    "canvas",
    "count",
    "kindButton",
    "selectedInfo",
    "labelInput",
    "selectedKind",
    "typeSelect",
  ];
  static values = { existing: Array, spotTypes: Array, saveUrl: String };

  static SNAP = 1.6; // Alignment snap threshold (%)
  static SPACING_SNAP = 2.0; // Equal-spacing snap threshold (%)
  static NEAR_AXIS = 6; // How close on the cross-axis to count as "same row/col" (%)
  static GUIDE_COLOR = "#f43f5e";
  static GUIDE_CENTER = "#a855f7";
  static SPACING_COLOR = "#3b82f6";

  connect() {
    this.spots = [];
    this.nextId = -1;
    this.activeTypeId = null;
    this.selectedSpotId = null;
    this.dragging = null;

    this.typesById = {};
    (this.spotTypesValue || []).forEach((t) => (this.typesById[t.id] = t));
    const types = this.spotTypesValue || [];
    if (types.length > 0) this.activeTypeId = types[0].id;

    this.loadExisting();
    this.updateKindButtons();
    this.render();

    this._down = this.onMouseDown.bind(this);
    this._move = this.onMouseMove.bind(this);
    this._up = this.onMouseUp.bind(this);
    this.canvasTarget.addEventListener("mousedown", this._down);
    window.addEventListener("mousemove", this._move);
    window.addEventListener("mouseup", this._up);
  }

  disconnect() {
    this.canvasTarget.removeEventListener("mousedown", this._down);
    window.removeEventListener("mousemove", this._move);
    window.removeEventListener("mouseup", this._up);
  }

  loadExisting() {
    (this.existingValue || []).forEach((s) =>
      this.spots.push({
        id: s.id,
        x: parseFloat(s.x),
        y: parseFloat(s.y),
        label: s.label,
        spotTypeId: s.spot_type_id,
      })
    );
  }

  typeFor(id) {
    return this.typesById[id] || { name: "Spot", color: "#6b7280" };
  }

  // ─── Mouse ───

  onMouseDown(e) {
    if (e.button !== 0 || !this.activeTypeId) return;
    const p = this.coords(e);
    const hit = this.spotAt(p.x, p.y);

    if (hit) {
      this.selectedSpotId = hit.id;
      this.dragging = { spot: hit };
      this.render();
    } else {
      const id = this.nextId--;
      const type = this.typeFor(this.activeTypeId);
      // New spots snap on placement exactly like dragged ones — rows, columns,
      // and spacing patterns lock in on the click, not only after a nudge.
      const snapped = this.computeSnap({ id }, p.x, p.y);
      this.spots.push({
        id,
        x: snapped.x,
        y: snapped.y,
        label: `${type.name} ${this.spots.length + 1}`,
        spotTypeId: this.activeTypeId,
      });
      this.selectedSpotId = id;
      this.render();
      this.renderOverlays(snapped.guides, snapped.measurements);
      clearTimeout(this._placeFlash);
      this._placeFlash = setTimeout(() => this.clearOverlays(), 700);
    }
  }

  onMouseMove(e) {
    if (!this.dragging) return;
    e.preventDefault();

    let { x, y } = this.coords(e);
    x = Math.min(97, Math.max(3, x));
    y = Math.min(97, Math.max(3, y));

    const result = this.computeSnap(this.dragging.spot, x, y);
    this.dragging.spot.x = result.x;
    this.dragging.spot.y = result.y;

    this.renderSpotElement(this.dragging.spot);
    this.renderOverlays(result.guides, result.measurements);
  }

  onMouseUp() {
    if (this.dragging) {
      this.dragging = null;
      this.clearOverlays();
    }
  }

  coords(e) {
    const r = this.canvasTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    };
  }

  spotAt(px, py) {
    let best = null,
      bestD = 4;
    for (const s of this.spots) {
      const d = Math.hypot(s.x - px, s.y - py);
      if (d < bestD) {
        best = s;
        bestD = d;
      }
    }
    return best;
  }

  // ─── Snap + Guides + Spacing ───

  computeSnap(dragSpot, rawX, rawY) {
    const T = this.constructor.SNAP;
    const guides = [];
    const measurements = [];
    let x = rawX,
      y = rawY;

    // Canvas center
    if (Math.abs(rawX - 50) < T) {
      x = 50;
      guides.push({ axis: "v", pos: 50, center: true });
    }
    if (Math.abs(rawY - 50) < T) {
      y = 50;
      guides.push({ axis: "h", pos: 50, center: true });
    }

    const others = this.spots.filter((s) => s.id !== dragSpot.id);

    // Alignment snap to other spots
    for (const o of others) {
      if (Math.abs(rawX - o.x) < T) {
        x = o.x;
        guides.push({ axis: "v", pos: o.x, center: false });
      }
      if (Math.abs(rawY - o.y) < T) {
        y = o.y;
        guides.push({ axis: "h", pos: o.y, center: false });
      }
    }

    // Spacing: find all spots on same row/col, show ALL adjacent gaps, snap to match
    const SS = this.constructor.SPACING_SNAP;
    const NA = this.constructor.NEAR_AXIS;

    // Horizontal axis: spots on same row
    const sameRow = others.filter((o) => Math.abs(o.y - y) < NA);
    if (sameRow.length >= 1) {
      const rowResult = this.computeAxisSpacing(
        sameRow.map((s) => s.x),
        x,
        SS
      );
      // Series continuation across rows: when this row alone can't establish
      // a rhythm (e.g. placing the SECOND spot of row 2), borrow the gaps
      // already used elsewhere on the floor — row 2 inherits row 1's spacing.
      if (rowResult.snapped === null) {
        const borrowed = this.borrowedGapSnap(sameRow.map((s) => s.x), x, others, "x", SS);
        if (borrowed !== null) {
          rowResult.snapped = borrowed.pos;
          rowResult.matchDist = borrowed.gap;
        }
      }
      if (rowResult.snapped !== null) x = rowResult.snapped;
      // Build measurement lines for ALL adjacent pairs including dragged spot
      const allX = [...sameRow.map((s) => s.x), x].sort((a, b) => a - b);
      for (let i = 0; i < allX.length - 1; i++) {
        const dist = allX[i + 1] - allX[i];
        if (dist > 1) {
          measurements.push({
            x1: allX[i],
            y1: y,
            x2: allX[i + 1],
            y2: y,
            dist,
            match: rowResult.matchDist !== null && Math.abs(dist - rowResult.matchDist) < 0.5,
          });
        }
      }
    }

    // Vertical axis: spots on same column
    const sameCol = others.filter((o) => Math.abs(o.x - x) < NA);
    if (sameCol.length >= 1) {
      const colResult = this.computeAxisSpacing(
        sameCol.map((s) => s.y),
        y,
        SS
      );
      if (colResult.snapped === null) {
        const borrowed = this.borrowedGapSnap(sameCol.map((s) => s.y), y, others, "y", SS);
        if (borrowed !== null) {
          colResult.snapped = borrowed.pos;
          colResult.matchDist = borrowed.gap;
        }
      }
      if (colResult.snapped !== null) y = colResult.snapped;
      const allY = [...sameCol.map((s) => s.y), y].sort((a, b) => a - b);
      for (let i = 0; i < allY.length - 1; i++) {
        const dist = allY[i + 1] - allY[i];
        if (dist > 1) {
          measurements.push({
            x1: x,
            y1: allY[i],
            x2: x,
            y2: allY[i + 1],
            dist,
            match: colResult.matchDist !== null && Math.abs(dist - colResult.matchDist) < 0.5,
          });
        }
      }
    }

    return { x, y, guides, measurements };
  }

  // Every adjacent gap used anywhere on the floor along one axis ("x" = gaps
  // within rows, "y" = gaps within columns). This is the spacing vocabulary
  // the gym has already established.
  axisGapLibrary(others, axis) {
    const NA = this.constructor.NEAR_AXIS;
    const main = axis === "x" ? (s) => s.x : (s) => s.y;
    const cross = axis === "x" ? (s) => s.y : (s) => s.x;
    const pool = [...others];
    const gaps = [];
    while (pool.length) {
      const seed = pool.shift();
      const cluster = [seed];
      for (let i = pool.length - 1; i >= 0; i--) {
        if (Math.abs(cross(pool[i]) - cross(seed)) < NA) cluster.push(pool.splice(i, 1)[0]);
      }
      const positions = cluster.map(main).sort((a, b) => a - b);
      for (let i = 0; i < positions.length - 1; i++) {
        const g = positions[i + 1] - positions[i];
        if (g > 2) gaps.push(g);
      }
    }
    return [...new Set(gaps.map((g) => Math.round(g * 10) / 10))];
  }

  // Snap adjacent to a same-row/col neighbor using a gap borrowed from the
  // rest of the floor. Returns { pos, gap } or null.
  borrowedGapSnap(neighborPositions, rawPos, others, axis, threshold) {
    let best = null;
    for (const gap of this.axisGapLibrary(others, axis)) {
      for (const n of neighborPositions) {
        for (const candidate of [n - gap, n + gap]) {
          if (candidate < 2 || candidate > 98) continue;
          const d = Math.abs(rawPos - candidate);
          if (d < threshold && (best === null || d < best.d)) {
            best = { pos: candidate, gap, d };
          }
        }
      }
    }
    return best ? { pos: best.pos, gap: best.gap } : null;
  }

  // Given existing positions on one axis and the dragged spot's raw position,
  // check if snapping to equal spacing is possible. Returns { snapped, matchDist }.
  computeAxisSpacing(existingPositions, rawPos, threshold) {
    const sorted = [...existingPositions].sort((a, b) => a - b);
    if (sorted.length < 2) return { snapped: null, matchDist: null };

    // Compute existing gaps between consecutive spots
    const gaps = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      gaps.push({ from: sorted[i], to: sorted[i + 1], dist: sorted[i + 1] - sorted[i] });
    }

    // Try snapping so the dragged spot creates a gap equal to an existing gap
    // For each pair of consecutive existing spots, try placing between or adjacent
    let bestSnap = null;
    let bestDist = null;

    for (const gap of gaps) {
      // Snap to create equal gap on left side of leftmost
      const leftSnap = sorted[0] - gap.dist;
      if (leftSnap > 2 && Math.abs(rawPos - leftSnap) < threshold) {
        bestSnap = leftSnap;
        bestDist = gap.dist;
      }
      // Snap to create equal gap on right side of rightmost
      const rightSnap = sorted[sorted.length - 1] + gap.dist;
      if (rightSnap < 98 && Math.abs(rawPos - rightSnap) < threshold) {
        bestSnap = rightSnap;
        bestDist = gap.dist;
      }
      // Snap to midpoint of each gap
      const midSnap = (gap.from + gap.to) / 2;
      if (Math.abs(rawPos - midSnap) < threshold) {
        bestSnap = midSnap;
        bestDist = gap.dist / 2;
      }
    }

    // Also check: for each existing gap, can we place between two spots
    // such that one of our gaps matches an existing gap?
    for (let i = 0; i < sorted.length - 1; i++) {
      for (const gap of gaps) {
        // Place so gap between sorted[i] and us equals gap.dist
        const snapRight = sorted[i] + gap.dist;
        if (
          snapRight < sorted[i + 1] - 1 &&
          Math.abs(rawPos - snapRight) < threshold
        ) {
          bestSnap = snapRight;
          bestDist = gap.dist;
        }
        // Place so gap between us and sorted[i+1] equals gap.dist
        const snapLeft = sorted[i + 1] - gap.dist;
        if (
          snapLeft > sorted[i] + 1 &&
          Math.abs(rawPos - snapLeft) < threshold
        ) {
          bestSnap = snapLeft;
          bestDist = gap.dist;
        }
      }
    }

    return { snapped: bestSnap, matchDist: bestDist };
  }

  // ─── Overlay Rendering ───

  renderOverlays(guides, measurements) {
    this.clearOverlays();
    const GC = this.constructor.GUIDE_COLOR;
    const CC = this.constructor.GUIDE_CENTER;
    const SC = this.constructor.SPACING_COLOR;

    // Guide lines
    for (const g of guides) {
      const el = document.createElement("div");
      el.setAttribute("data-overlay", "1");
      const color = g.center ? CC : GC;

      if (g.axis === "v") {
        Object.assign(el.style, {
          position: "absolute",
          left: `${g.pos}%`,
          top: "0",
          width: "1px",
          height: "100%",
          backgroundColor: color,
          opacity: "0.5",
          pointerEvents: "none",
          zIndex: "10",
        });
      } else {
        Object.assign(el.style, {
          position: "absolute",
          top: `${g.pos}%`,
          left: "0",
          height: "1px",
          width: "100%",
          backgroundColor: color,
          opacity: "0.5",
          pointerEvents: "none",
          zIndex: "10",
        });
      }
      this.canvasTarget.appendChild(el);
    }

    // Measurement lines + labels for ALL adjacent gaps
    const MATCH_COLOR = "#22c55e"; // Green for equal-spaced gaps

    for (const m of measurements) {
      const isHoriz = Math.abs(m.y1 - m.y2) < 0.5;
      const color = m.match ? MATCH_COLOR : SC;

      // Line
      const line = document.createElement("div");
      line.setAttribute("data-overlay", "1");

      if (isHoriz) {
        Object.assign(line.style, {
          position: "absolute",
          left: `${m.x1}%`,
          top: `${m.y1}%`,
          width: `${m.x2 - m.x1}%`,
          height: m.match ? "2px" : "1px",
          backgroundColor: color,
          opacity: m.match ? "0.8" : "0.5",
          pointerEvents: "none",
          zIndex: "10",
          transform: "translateY(-50%)",
        });
      } else {
        Object.assign(line.style, {
          position: "absolute",
          left: `${m.x1}%`,
          top: `${m.y1}%`,
          width: m.match ? "2px" : "1px",
          height: `${m.y2 - m.y1}%`,
          backgroundColor: color,
          opacity: m.match ? "0.8" : "0.5",
          pointerEvents: "none",
          zIndex: "10",
          transform: "translateX(-50%)",
        });
      }
      this.canvasTarget.appendChild(line);

      // Distance label
      const label = document.createElement("div");
      label.setAttribute("data-overlay", "1");
      const rounded = Math.round(m.dist * 10) / 10;

      Object.assign(label.style, {
        position: "absolute",
        pointerEvents: "none",
        zIndex: "11",
        fontSize: "9px",
        fontWeight: "700",
        fontFamily: "monospace",
        color: "white",
        backgroundColor: color,
        borderRadius: "3px",
        padding: "1px 5px",
        lineHeight: "1.4",
        transform: "translate(-50%, -50%)",
      });

      if (isHoriz) {
        label.style.left = `${(m.x1 + m.x2) / 2}%`;
        label.style.top = `calc(${m.y1}% - 10px)`;
      } else {
        label.style.left = `calc(${m.x1}% + 12px)`;
        label.style.top = `${(m.y1 + m.y2) / 2}%`;
      }
      label.textContent = rounded;
      this.canvasTarget.appendChild(label);
    }
  }

  clearOverlays() {
    this.canvasTarget
      .querySelectorAll("[data-overlay]")
      .forEach((el) => el.remove());
  }

  // ─── Palette ───

  // Palette buttons arm the placement brush — and ONLY that. Changing an
  // existing spot's type is an explicit act via the inspector dropdown
  // (changeSelectedType); the old behavior silently retyped whatever spot
  // was selected (usually the one you just placed) when you switched
  // brushes for the next row.
  setKind(e) {
    const typeId = parseInt(e.currentTarget.dataset.spotTypeId);
    this.activeTypeId = typeId;
    this.selectedSpotId = null;
    this.updateKindButtons();
    this.render();
  }

  // Inspector dropdown: retype the selected spot, explicitly.
  changeSelectedType(e) {
    if (this.selectedSpotId === null) return;
    const spot = this.spots.find((s) => s.id === this.selectedSpotId);
    if (!spot) return;
    spot.spotTypeId = parseInt(e.currentTarget.value);
    this.render();
  }

  updateKindButtons() {
    this.kindButtonTargets.forEach((btn) => {
      const active =
        parseInt(btn.dataset.spotTypeId) === this.activeTypeId;
      btn.classList.toggle("ring-2", active);
      btn.classList.toggle("ring-primary-500", active);
      btn.classList.toggle("ring-offset-2", active);
      btn.classList.toggle("dark:ring-offset-gray-900", active);
      btn.classList.toggle("scale-105", active);
      btn.classList.toggle("shadow-md", active);
    });
  }

  // ─── Selected Spot ───

  updateLabel() {
    if (!this.hasLabelInputTarget || this.selectedSpotId === null) return;
    const spot = this.spots.find((s) => s.id === this.selectedSpotId);
    if (spot) {
      spot.label = this.labelInputTarget.value;
      this.renderSpotElement(spot);
    }
  }

  deleteSelected() {
    if (this.selectedSpotId === null) return;
    this.spots = this.spots.filter((s) => s.id !== this.selectedSpotId);
    this.selectedSpotId = null;
    this.render();
  }

  // ─── Rendering ───

  render() {
    this.canvasTarget
      .querySelectorAll("[data-spot]")
      .forEach((el) => el.remove());
    this.clearOverlays();
    this.spots.forEach((s) => this.createSpotElement(s));
    this.updateCount();
    this.updateSelectedInfo();
  }

  createSpotElement(spot) {
    const el = document.createElement("div");
    el.setAttribute("data-spot", spot.id);
    this.applySpotStyles(el, spot);
    this.canvasTarget.appendChild(el);
  }

  renderSpotElement(spot) {
    const el = this.canvasTarget.querySelector(
      `[data-spot="${spot.id}"]`
    );
    if (el) this.applySpotStyles(el, spot);
    this.updateSelectedInfo();
  }

  applySpotStyles(el, spot) {
    const type = this.typeFor(spot.spotTypeId);
    const sel = spot.id === this.selectedSpotId;

    Object.assign(el.style, {
      position: "absolute",
      left: `${spot.x}%`,
      top: `${spot.y}%`,
      transform: "translate(-50%, -50%)",
      width: "3.5rem",
      height: "3.5rem",
      borderRadius: "0.75rem",
      backgroundColor: type.color,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "grab",
      userSelect: "none",
      zIndex: sel ? "20" : "1",
      transition: this.dragging ? "none" : "box-shadow 0.15s",
      boxShadow: sel
        ? `0 0 0 3px white, 0 0 0 5px ${type.color}, 0 4px 12px rgba(0,0,0,0.15)`
        : "0 2px 4px rgba(0,0,0,0.15)",
    });

    el.innerHTML = `
      <span style="font-size:11px;font-weight:700;color:white;line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:3rem;text-align:center">${this.esc(spot.label)}</span>
      <span style="font-size:8px;color:rgba(255,255,255,0.65);margin-top:1px">${this.esc(type.name)}</span>
    `;
  }

  updateCount() {
    if (!this.hasCountTarget) return;
    const n = this.spots.length;
    this.countTarget.textContent = `${n} spot${n === 1 ? "" : "s"}`;
  }

  updateSelectedInfo() {
    if (!this.hasSelectedInfoTarget) return;
    const spot = this.spots.find((s) => s.id === this.selectedSpotId);
    if (spot) {
      const type = this.typeFor(spot.spotTypeId);
      this.selectedInfoTarget.classList.remove("hidden");
      if (this.hasLabelInputTarget) this.labelInputTarget.value = spot.label;
      if (this.hasSelectedKindTarget) {
        this.selectedKindTarget.innerHTML = `
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${type.color};vertical-align:middle"></span>
        `;
      }
      if (this.hasTypeSelectTarget) this.typeSelectTarget.value = String(spot.spotTypeId ?? "");
    } else {
      this.selectedInfoTarget.classList.add("hidden");
    }
  }

  esc(s) {
    const d = document.createElement("div");
    d.textContent = s;
    return d.innerHTML;
  }

  // ─── Save ───

  save() {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = this.saveUrlValue;
    form.style.display = "none";

    const csrf = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content;
    if (csrf) this.hid(form, "authenticity_token", csrf);
    this.hid(form, "_method", "PATCH");

    this.spots.forEach((spot, i) => {
      if (spot.id > 0) this.hid(form, `spots[${i}][id]`, spot.id);
      this.hid(form, `spots[${i}][x]`, spot.x.toFixed(2));
      this.hid(form, `spots[${i}][y]`, spot.y.toFixed(2));
      this.hid(form, `spots[${i}][label]`, spot.label);
      this.hid(form, `spots[${i}][spot_type_id]`, spot.spotTypeId || "");
    });

    document.body.appendChild(form);
    form.submit();
  }

  hid(form, name, value) {
    const i = document.createElement("input");
    i.type = "hidden";
    i.name = name;
    i.value = value;
    form.appendChild(i);
  }
}
